import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreVertical, Trash2, FolderOpen } from 'lucide-react';
import { Session } from '../../../types/chat';
import { formatRelativeTime } from '../../../utils/formatters';
import { DropdownMenu, DropdownMenuItem } from '../../common';

interface ConversationCardProps {
  conversation: Session;
  onDelete: (id: number) => Promise<void>;
}

/**
 * ConversationCard - Card de conversa individual
 *
 * Exibe:
 * - Título da conversa
 * - Preview da primeira mensagem
 * - Badge do projeto (se vinculado)
 * - Tempo relativo
 * - Menu de contexto
 */
export const ConversationCard = ({ conversation, onDelete }: ConversationCardProps) => {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleClick = () => {
    // Check if conversation is part of a project
    if (conversation.project_id) {
      navigate(`/project/${conversation.project_id}/chat/${conversation.id}`);
    } else {
      navigate(`/chat/${conversation.id}`);
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);

    try {
      setIsDeleting(true);
      await onDelete(conversation.id);
    } catch (error) {
      console.error('Error deleting conversation:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const menuItems: DropdownMenuItem[] = [
    {
      label: 'Excluir',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDelete,
      variant: 'danger',
    },
  ];

  return (
    <div
      onClick={handleClick}
      className={`
        relative bg-[#1a1a1a] rounded-xl p-4 cursor-pointer
        border border-[#333333] hover:border-[#F4CC04]
        transition-all duration-200
        ${isDeleting ? 'opacity-50 pointer-events-none' : ''}
      `}
    >
      {/* Content */}
      <div className="flex justify-between items-start gap-4">
        {/* Left: Title and preview */}
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-medium text-base mb-1 truncate">
            {conversation.chat_title || 'Novo chat'}
          </h3>

          {/* Preview - shows message count */}
          <p className="text-[#666666] text-sm line-clamp-1">
            {conversation.messages_count > 0
              ? `${conversation.messages_count} mensagen${conversation.messages_count === 1 ? '' : 's'}`
              : 'Sem mensagens'
            }
          </p>

          {/* Project badge */}
          {conversation.project_id && conversation.project_name && (
            <div className="flex items-center gap-1.5 mt-2">
              <FolderOpen className="w-3.5 h-3.5 text-[#F4CC04]" />
              <span className="text-xs text-[#F4CC04] font-medium">
                {conversation.project_name}
              </span>
            </div>
          )}
        </div>

        {/* Right: Time and menu */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[#666666] text-sm">
            {formatRelativeTime(conversation.last_access || conversation.created_at)}
          </span>

          {/* Menu button */}
          <div className="relative">
            <button
              onClick={handleMenuClick}
              className="p-1 rounded-lg text-[#666666] hover:text-white hover:bg-[#333333] transition-colors"
            >
              <MoreVertical className="w-5 h-5" />
            </button>

            <DropdownMenu
              isOpen={showMenu}
              onClose={() => setShowMenu(false)}
              items={menuItems}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
